import { Sidepanel } from 'components';
import CheckField from 'components/CheckField';
import { Api } from 'lib';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { ContentHeader, SearchUsers } from 'screens/Admin/components';
import {
  buildCampaignPayload,
  campaignToForm,
  editableFields,
  INITIAL_FORM,
  pickEditable,
  toggleFeature,
  validateCampaignForm
} from './form';
import { formatJst, formatUtc, fromJstInput } from './time';

const inputClass =
  'w-full border border-[#B0B8C4] rounded-md px-3 py-2.5 text-[13px] text-[#20252D] placeholder-[#9CA3AF] bg-[#F9FAFB] focus:bg-white focus:outline-none focus:border-[#7989A0] focus:ring-1 focus:ring-[#B5BDCA] transition-colors disabled:opacity-60 disabled:cursor-not-allowed';
const labelClass = 'block text-[10px] font-bold text-gray-600 uppercase tracking-wider mb-1';
const errorClass = 'text-[#FA3F58] text-[11px] mt-1 font-medium';
const hintClass = 'text-[11px] text-[#97A3B5] mt-1';
const K = 'subscription.admin.campaigns';

const CampaignPanel = ({ visible, closeModal, campaign, features, onSaved }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [conflicts, setConflicts] = useState([]);

  const isEditing = !!campaign;
  const status = campaign?.status ?? 'scheduled';
  const editable = editableFields(status);
  const can = (field) => editable.includes(field);

  useEffect(() => {
    setForm(campaign ? campaignToForm(campaign) : INITIAL_FORM);
    setErrors({});
    setConflicts([]);
  }, [campaign, visible]);

  const update = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    // Whatever was in the way may no longer be once the dates or teams change.
    setConflicts([]);
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }));
  };

  const addTeam = (team) => {
    if (!team || form.teams.some((x) => x._id === team._id)) return;
    update('teams', [...form.teams, team]);
  };

  async function handleSave() {
    const found = validateCampaignForm(form);
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    setSaving(true);
    const payload = buildCampaignPayload(form);
    // Silent, so an overlap (409) can be shown against the form instead of as a toast; every
    // other failure is reported below, because silent also suppresses the global error toast.
    const res = isEditing
      ? await Api.patch(`/admin/featureCampaigns/update/${campaign._id}`, pickEditable(payload, status), undefined, {
          silent: true
        })
      : await Api.post('/admin/featureCampaigns/create', payload, undefined, { silent: true });
    setSaving(false);

    if (res?.status === 409 && res.conflicts?.length) {
      setConflicts(res.conflicts);
      return;
    }
    if (!res || res.err) {
      if (res?.err) dispatch({ type: 'HANDLE_RES', payload: { err: res.err } });
      return;
    }

    dispatch({
      type: 'HANDLE_RES',
      payload: { res: t(isEditing ? `${K}.success.saved` : `${K}.success.created`) }
    });
    onSaved?.();
    closeModal();
  }

  const title = status === 'ended' ? `${K}.panel.viewTitle` : isEditing ? `${K}.panel.editTitle` : `${K}.panel.createTitle`;
  const utcEcho = formatUtc(fromJstInput(form.endsAt));

  return (
    <Sidepanel wid="640" pad="0" visible={visible} closeModal={closeModal}>
      <ContentHeader text={t(title)} closePanel={closeModal} />
      <div className="px-6 py-5 space-y-4">
        {status === 'active' && <p className="text-[12px] text-[#5F6F86]">{t(`${K}.lockedHint`)}</p>}
        {status === 'ended' && <p className="text-[12px] text-[#5F6F86]">{t(`${K}.endedHint`)}</p>}

        {conflicts.length > 0 && (
          <div role="alert" className="rounded-md border border-[#FCA5A5] bg-[#FEF2F2] px-3 py-2.5 space-y-2">
            <p className="text-[12px] font-semibold text-[#B91C1C]">{t(`${K}.conflict.title`)}</p>
            {conflicts.map((c) => (
              <div key={c.id} className="text-[12px] text-[#7F1D1D]">
                <span className="font-semibold">{c.name}</span>
                <span data-conflict-teams className="ml-1">
                  {c.allTeams ? t(`${K}.conflict.allTeams`) : c.teams.map((team) => team.name).join(', ')}
                </span>
                <div className="text-[11px] text-[#991B1B]">
                  {formatJst(c.startsAt)} – {formatJst(c.endsAt)}
                </div>
              </div>
            ))}
          </div>
        )}

        <div>
          <label htmlFor="campaign-name" className={labelClass}>{t(`${K}.field.name`)}</label>
          <input
            id="campaign-name"
            className={inputClass}
            value={form.name}
            disabled={!can('name')}
            onChange={(e) => update('name', e.target.value)}
          />
          {errors.name && <p className={errorClass}>{t(errors.name)}</p>}
        </div>

        <fieldset>
          <legend className={labelClass}>{t(`${K}.field.features`)}</legend>
          {features.map((f) => (
            <CheckField
              key={f.key}
              checked={form.features.includes(f.key)}
              disabled={!can('features')}
              onChange={() => update('features', toggleFeature(form.features, f.key))}
            >
              {f.label}
            </CheckField>
          ))}
          {errors.features && <p className={errorClass}>{t(errors.features)}</p>}
        </fieldset>

        <fieldset>
          <legend className={labelClass}>{t(`${K}.field.teams`)}</legend>
          <CheckField
            checked={form.allTeams}
            disabled={!can('scope')}
            onChange={(e) => update('allTeams', e.target.checked)}
          >
            {t(`${K}.field.allTeams`)}
          </CheckField>
          {!form.allTeams && can('scope') && <SearchUsers userType="team" selected={null} handleSelect={addTeam} />}
          {!form.allTeams &&
            form.teams.map((team) => (
              <span
                key={team._id}
                className="inline-flex items-center gap-1 mr-2 mt-2 px-2 py-1 rounded bg-[#F0F2F4] text-[12px] text-[#353E4A]"
              >
                {team.name}
                {can('scope') && (
                  <button
                    type="button"
                    aria-label={t(`${K}.removeTeam`, { name: team.name })}
                    className="text-[#97A3B5] hover:text-[#FA3F58]"
                    onClick={() => update('teams', form.teams.filter((x) => x._id !== team._id))}
                  >
                    ×
                  </button>
                )}
              </span>
            ))}
          {errors.teams && <p className={errorClass}>{t(errors.teams)}</p>}
        </fieldset>

        <div>
          <label htmlFor="campaign-starts" className={labelClass}>{t(`${K}.field.startsAt`)}</label>
          <input
            id="campaign-starts"
            type="datetime-local"
            className={inputClass}
            value={form.startsAt}
            disabled={!can('startsAt')}
            onChange={(e) => update('startsAt', e.target.value)}
          />
          {errors.startsAt && <p className={errorClass}>{t(errors.startsAt)}</p>}
        </div>

        <div>
          <label htmlFor="campaign-ends" className={labelClass}>{t(`${K}.field.endsAt`)}</label>
          <input
            id="campaign-ends"
            type="datetime-local"
            className={inputClass}
            value={form.endsAt}
            disabled={!can('endsAt')}
            onChange={(e) => update('endsAt', e.target.value)}
          />
          <p className={hintClass}>{t(`${K}.jstHint`)}</p>
          {utcEcho && <p className="text-[11px] text-[#97A3B5]">{utcEcho}</p>}
          {errors.endsAt && <p className={errorClass}>{t(errors.endsAt)}</p>}
        </div>

        {status !== 'ended' && (
          <div className="pt-2 pb-2">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="w-full flex items-center justify-center gap-2 py-3 text-[14px] font-semibold text-white bg-[#20252D] rounded-md hover:bg-[#353E4A] active:scale-[0.99] disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            >
              {saving ? t(`${K}.button.saving`) : t(isEditing ? `${K}.button.save` : `${K}.button.create`)}
            </button>
          </div>
        )}
      </div>
    </Sidepanel>
  );
};

export default CampaignPanel;
